/**
 * CR5 — Non-destructive CV parsing (compare engine + apply helpers).
 *
 * Single, self-contained module (kept as one file to minimise the change
 * footprint). It does three things, with NO React/Redux dependency:
 *   1. buildCvSuggestions()  – compare parsed CV data vs the current form and
 *      sort every value into: empty→auto-fill, non-empty-differs→conflict,
 *      brand-new collection row→new item.
 *   2. buildAutoFillPayload() – turn the safe auto-fills back into the parser
 *      shape so the existing fillingContactForm() can apply them (no overwrite).
 *   3. applyConflictValue()/acceptNewItem() – apply a single reviewed choice,
 *      reusing the candidate reducer actions that ALREADY exist.
 *
 * Suggestions are ephemeral: they live in local component state and the popup,
 * never in the DB. No backend/migration changes.
 */

/* ------------------------------------------------------------------ types -- */

export type ScalarSection =
  | "profileCard"
  | "general"
  | "jobProfile"
  | "other"
  | "internalInfo"
  | "workPermit";

export type CollectionSection =
  | "studies"
  | "workExperience"
  | "certificates"
  | "competences";

export interface FieldSuggestion {
  section: ScalarSection;
  field: string;
  existing: any;
  parsed: any;
}

export interface CollectionSuggestion {
  section: CollectionSection;
  key: string; // stable identity for dedupe + resolving in the popup
  item: any;
}

export interface CvSuggestions {
  autoFillFields: FieldSuggestion[];
  autoFillCollections: CollectionSuggestion[];
  conflicts: FieldSuggestion[];
  newItems: CollectionSuggestion[];
}

const emptyCvSuggestions = (): CvSuggestions => ({
  autoFillFields: [],
  autoFillCollections: [],
  conflicts: [],
  newItems: [],
});

/**
 * True only when there are conflicts to review. New entries are auto-added on
 * parse (not shown in the popup), so they no longer trigger it.
 */
export const hasReviewableSuggestions = (s: CvSuggestions | null): boolean =>
  !!s && s.conflicts.length > 0;

/* ------------------------------------------------------- comparison utils -- */

const PHONE_FIELDS = new Set(["mobNumber", "contactPhone", "refPhone"]);
const DATE_FIELDS = new Set([
  "dateOfBirth", "from", "to", "exp_from", "exp_to",
  "valid", "validUntill", "perFrom", "perTo", "date",
]);

/**
 * Internal / derived / non-user fields the parser must never surface or change.
 * e.g. `domCheckbox` is the "Same as residence" toggle (the parser always sends
 * false → it would otherwise show as a conflict and, if applied, split the
 * addresses); `age` is derived from DOB; the rest are system flags.
 */
const IGNORE_FIELDS = new Set<string>([
  "domCheckbox", "age",
  "sourceId", "formStatus", "candidateStatus", "userId",
  "electronicDoc", "esign", "score", "consentToTerms",
  "internalEmployee", "hiddenEmployee", "contractReady",
  "contractSeasonalWorker", "payRoll", "initialLat", "initialLn",
]);

/**
 * "Empty" (safe to auto-fill): null/undefined, blank string, or empty array.
 * Numbers (incl. 0), booleans and populated objects are NOT empty — they are a
 * real choice and must be protected from overwrite.
 */
const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  // Dropdown/select placeholders (e.g. { value: null, label: "Select" }) or an
  // empty object count as empty so they auto-fill instead of showing a conflict.
  if (typeof value === "object" && !(value instanceof Date)) {
    if ("value" in value) {
      const v = (value as any).value;
      return v === null || v === undefined || v === "";
    }
    return Object.keys(value).length === 0;
  }
  return false;
};

const stripDiacritics = (input: string): string =>
  input.normalize("NFD").replace(/[̀-ͯ]/g, "");

const normalizePhone = (input: string): string =>
  input.replace(/[^\d+]/g, "").replace(/(?!^)\+/g, "");

const normalizeDate = (value: any): string => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = new Date(value);
    if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
    return value.trim();
  }
  return "";
};

/** Canonical comparable string so different representations compare equal. */
const getComparable = (value: any, field?: string): string => {
  if (value === null || value === undefined) return "";
  if (field && DATE_FIELDS.has(field)) return normalizeDate(value);
  if (typeof value === "object" && !(value instanceof Date)) {
    if (Array.isArray(value)) {
      return value.map((v) => getComparable(v, field)).sort().join(",");
    }
    if ("value" in value) return getComparable((value as any).value, field);
    if ("id" in value) return getComparable((value as any).id, field);
    return JSON.stringify(value);
  }
  if (value instanceof Date) return normalizeDate(value);
  const str = String(value).trim();
  if (field && PHONE_FIELDS.has(field)) return normalizePhone(str);
  return stripDiacritics(str.toLowerCase());
};

const valuesEqual = (a: any, b: any, field?: string): boolean =>
  getComparable(a, field) === getComparable(b, field);

const hasAnyMeaningful = (item: Record<string, any>, fields: string[]): boolean =>
  fields.some((f) => !isEmptyValue(item?.[f]));

const buildItemKey = (item: Record<string, any>, keyFields: string[]): string =>
  keyFields.map((f) => getComparable(item?.[f], f)).join("|");

/**
 * Age is a UI-derived field (the date picker computes it on change). When we
 * apply a date of birth from the CV programmatically those handlers don't run,
 * so we recompute age here using the same formula (currentYear - birthYear).
 */
const computeAge = (dob: any): number | "" => {
  if (!dob) return "";
  const d = dob instanceof Date ? dob : new Date(dob);
  if (isNaN(d.getTime())) return "";
  return new Date().getFullYear() - d.getFullYear();
};

/* --------------------------------------------------------- section config -- */

const SCALAR_SECTIONS: { parsedKey: string; stateKey: ScalarSection }[] = [
  { parsedKey: "profileCardDetails", stateKey: "profileCard" },
  { parsedKey: "generalDetails", stateKey: "general" },
  { parsedKey: "jobProfileDetails", stateKey: "jobProfile" },
  { parsedKey: "otherDetails", stateKey: "other" },
  { parsedKey: "internalInfoDetails", stateKey: "internalInfo" },
  { parsedKey: "workPermit", stateKey: "workPermit" },
];

interface CollectionCfg {
  parsedKey: string;
  stateKey: "studies" | "workExperience" | "certificates";
  keyFields: string[];
  meaningfulFields: string[];
}

const COLLECTION_SECTIONS: CollectionCfg[] = [
  { parsedKey: "studyDetails", stateKey: "studies",
    keyFields: ["school", "diploma", "from"],
    meaningfulFields: ["school", "study", "diploma", "from", "to"] },
  // exp_from is deliberately NOT a key field: a date edited by hand (or reported
  // differently on a re-parse) would otherwise stop a parsed row from matching
  // the equivalent row already on the tab, and re-parsing would duplicate it.
  { parsedKey: "workDetails", stateKey: "workExperience",
    keyFields: ["employer", "title"],
    meaningfulFields: ["employer", "title", "description", "exp_from"] },
  { parsedKey: "certificateDetails", stateKey: "certificates",
    keyFields: ["attestType", "documentNo"],
    meaningfulFields: ["attestType", "documentNo", "group"] },
];

/* ----------------------------------------------------------- build engine -- */

const compareScalar = (
  parsedBucket: Record<string, any>,
  stateSlice: Record<string, any>,
  stateKey: ScalarSection,
  out: CvSuggestions
): void => {
  if (!parsedBucket || typeof parsedBucket !== "object") return;
  Object.entries(parsedBucket).forEach(([field, parsed]) => {
    if (IGNORE_FIELDS.has(field)) return; // internal/derived flags — never touch
    if (isEmptyValue(parsed)) return; // parser found nothing → never touch form
    const existing = stateSlice ? stateSlice[field] : undefined;
    if (isEmptyValue(existing)) {
      out.autoFillFields.push({ section: stateKey, field, existing, parsed });
    } else if (!valuesEqual(existing, parsed, field)) {
      out.conflicts.push({ section: stateKey, field, existing, parsed });
    }
  });
};

const compareCollection = (
  parsedRows: any[],
  existingRows: any[],
  cfg: CollectionCfg,
  out: CvSuggestions
): void => {
  if (!Array.isArray(parsedRows) || parsedRows.length === 0) return;
  const meaningfulExisting = (existingRows ?? []).filter((r) =>
    hasAnyMeaningful(r ?? {}, cfg.meaningfulFields)
  );
  const fresh = parsedRows.filter((r) => hasAnyMeaningful(r ?? {}, cfg.meaningfulFields));

  // No real existing data → apply parsed rows directly (pre-CR5 behaviour).
  if (meaningfulExisting.length === 0) {
    fresh.forEach((item) =>
      out.autoFillCollections.push({
        section: cfg.stateKey, key: buildItemKey(item, cfg.keyFields), item,
      })
    );
    return;
  }
  // Existing data present → only brand-new rows become review suggestions.
  const existingKeys = new Set(
    meaningfulExisting.map((r) => buildItemKey(r, cfg.keyFields))
  );
  fresh.forEach((item) => {
    const key = buildItemKey(item, cfg.keyFields);
    if (!existingKeys.has(key)) {
      out.newItems.push({ section: cfg.stateKey, key, item });
    }
  });
};

/**
 * A competence child counts as "set" only when the parser gave it a real
 * rating. The competence catalog ships every possible child with rating -1
 * (meaning "not selected"), so name-presence alone is NOT enough — we must
 * compare the rating to know whether the candidate actually has it.
 */
const competenceRating = (child: any): number => {
  const r = child?.candidate_competence_details?.rating;
  return r === undefined || r === null ? -1 : r;
};

const compareCompetences = (
  parsedCompetences: any,
  existingCompetences: any,
  out: CvSuggestions
): void => {
  const parsedParents: any[] = parsedCompetences?.competence ?? [];
  if (!Array.isArray(parsedParents) || parsedParents.length === 0) return;
  const existingParents: any[] = existingCompetences?.competence ?? [];

  // No existing competences at all → apply the parsed set directly (pre-CR5).
  if (!Array.isArray(existingParents) || existingParents.length === 0) {
    out.autoFillCollections.push({
      section: "competences", key: "competences:all", item: parsedCompetences,
    });
    return;
  }
  const existingByName = new Map<string, any>();
  existingParents.forEach((p) => existingByName.set(getComparable(p?.competence_name), p));

  parsedParents.forEach((parent) => {
    const nameKey = getComparable(parent?.competence_name);
    // Only the children the parser actually selected (rating !== -1) are data.
    const selected = (parent.child_competences ?? []).filter(
      (c: any) => competenceRating(c) !== -1
    );
    if (selected.length === 0) return;

    const existingParent = existingByName.get(nameKey);
    const existingByChild = new Map<string, any>();
    (existingParent?.child_competences ?? []).forEach((c: any) =>
      existingByChild.set(getComparable(c?.name), c)
    );

    selected.forEach((child: any) => {
      const childKey = getComparable(child?.name);
      // Suggest only when the candidate doesn't already have a rating for it,
      // so an existing competence rating is never overwritten automatically.
      if (competenceRating(existingByChild.get(childKey)) === -1) {
        out.newItems.push({
          section: "competences",
          key: `competences:child:${nameKey}:${childKey}`,
          item: { type: "child", competenceName: parent.competence_name, child },
        });
      }
    });
  });
};

/** Compare parsed CV data against the current form. Mutates nothing. */
export const buildCvSuggestions = (
  parsedData: Record<string, any>,
  state: Record<string, any>
): CvSuggestions => {
  const out = emptyCvSuggestions();
  if (!parsedData || !state) return out;
  SCALAR_SECTIONS.forEach(({ parsedKey, stateKey }) =>
    compareScalar(parsedData[parsedKey], state[stateKey], stateKey, out)
  );
  COLLECTION_SECTIONS.forEach((cfg) =>
    compareCollection(parsedData[cfg.parsedKey], state[cfg.stateKey], cfg, out)
  );
  compareCompetences(parsedData.competences, state.competences, out);
  return out;
};

/* -------------------------------------------------- auto-fill (no overwrite) */

const SECTION_TO_PARSED_KEY: Record<string, string> = {
  profileCard: "profileCardDetails",
  general: "generalDetails",
  jobProfile: "jobProfileDetails",
  other: "otherDetails",
  internalInfo: "internalInfoDetails",
  workPermit: "workPermit",
};
const COLLECTION_TO_PARSED_KEY: Record<string, string> = {
  studies: "studyDetails",
  workExperience: "workDetails",
  certificates: "certificateDetails",
};

/**
 * Build a parser-shaped object containing ONLY the safe auto-fills (empty
 * fields + empty collections). Feeding this to fillingContactForm can never
 * overwrite existing data.
 */
export const buildAutoFillPayload = (
  s: CvSuggestions
): { payload: Record<string, any>; competences: any | null } => {
  const payload: Record<string, any> = {};
  s.autoFillFields.forEach(({ section, field, parsed }) => {
    const key = SECTION_TO_PARSED_KEY[section];
    if (!key) return;
    (payload[key] = payload[key] || {})[field] = parsed;
  });
  let competences: any | null = null;
  s.autoFillCollections.forEach(({ section, item }) => {
    if (section === "competences") { competences = item; return; }
    const key = COLLECTION_TO_PARSED_KEY[section];
    if (!key) return;
    (payload[key] = payload[key] || []).push(item);
  });

  // CR5: when date of birth is auto-filled, derive age too (it isn't a parsed
  // field and is normally computed by the date picker).
  if (payload.generalDetails && payload.generalDetails.dateOfBirth) {
    payload.generalDetails.age = computeAge(payload.generalDetails.dateOfBirth);
  }

  return { payload, competences };
};

/**
 * Residence address fields that have a "dom"-prefixed domicile counterpart.
 * The domicile field name is derived (not hardcoded) using the same convention
 * the form uses: "dom" + capitalised field (e.g. zipCode -> domZipCode).
 */
const RESIDENCE_ADDRESS_FIELDS = [
  "street", "number", "box", "zipCode", "cityOther", "city", "country",
];

const toDomicileField = (field: string): string =>
  "dom" + field.charAt(0).toUpperCase() + field.slice(1);

/**
 * When "Same as residence address" (domCheckbox) is on, the form copies each
 * residence field to its domicile counterpart on manual entry. CV auto-fill
 * sets residence fields programmatically and bypasses that, leaving domicile
 * empty — so mirror them here. Mutates the general payload in place and only
 * fills a dom* field that isn't already present.
 */
export const mirrorResidenceToDomicile = (generalPayload: Record<string, any>): void => {
  if (!generalPayload) return;
  RESIDENCE_ADDRESS_FIELDS.forEach((field) => {
    const domicile = toDomicileField(field);
    if (generalPayload[field] !== undefined && generalPayload[domicile] === undefined) {
      generalPayload[domicile] = generalPayload[field];
    }
  });
};

/* --------------------------------------------- apply a single reviewed item */

const SCALAR_ACTION: Record<string, string> = {
  profileCard: "UPDATE_PROFILE_FIELD",
  general: "UPDATE_GENERAL_FIELD",
  jobProfile: "UPDATE_JOB_PROFILE_FIELD",
  other: "UPDATE_OTHER_FIELD",
  internalInfo: "UPDATE_INTERNAL_INFO_FIELD",
  workPermit: "UPDATE_PERMIT_FIELD",
};
const SCALAR_DATE_FIELDS = new Set(["dateOfBirth", "perFrom", "perTo", "date"]);

const toDate = (field: string, value: any, dateFields: Set<string>): any => {
  if (dateFields.has(field) && typeof value === "string" && value.trim() !== "") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d;
  }
  return value;
};

/** Apply a conflicting field's CV value (user chose "Use CV"). */
export const applyConflictValue = (
  section: string, field: string, parsed: any, dispatch: any, state?: any
): void => {
  const type = SCALAR_ACTION[section];
  if (!type) return;
  const value = toDate(field, parsed, SCALAR_DATE_FIELDS);
  dispatch({ type, field, value });
  // CR5: keep derived age in sync when the DOB is taken from the CV.
  if (section === "general" && field === "dateOfBirth") {
    dispatch({ type, field: "age", value: computeAge(parsed) });
  }
  // CR5: when a residence address field is taken from the CV and "Same as
  // residence address" is on, mirror it to the domicile field too.
  if (
    section === "general" &&
    state?.general?.domCheckbox &&
    RESIDENCE_ADDRESS_FIELDS.includes(field)
  ) {
    dispatch({ type, field: toDomicileField(field), value });
  }
};

/** Default shapes so an appended row always has every field the form expects. */
const DEFAULT_STUDY: Record<string, any> = {
  id: null, diploma: null, school: "", study: "", from: null, to: null,
  info: "", completed: 1, certificateId: null, fileName: "", filePath: "",
};
const DEFAULT_WORK: Record<string, any> = {
  id: null, title: "", domain: null, function: null, employer: "", description: "",
  exp_from: null, exp_to: null, referenceId: null, reference: 0,
  includeReasonInCv: 0, excludeWorkExperienceInEmail: 0, fName: "", lName: "",
  refEmail: "", refPhone: "", referenceTaken: "", referenceInfo: "",
  reasonForDeparture: null, reason: "", workExperience: 1,
  fileId: null, fileName: "", filePath: "",
};

/**
 * Accept a new collection suggestion (user clicked "Add" or "Apply all").
 *
 * Each entry is applied through a dedicated reducer action that appends/merges
 * against the LIVE state. This is the fix for "Education: nothing is taken":
 * the old version computed an array index / cloned competences from a captured
 * `state` snapshot, so adding several items in one go (Apply all) made them all
 * write to the same index / overwrite the same clone — only one survived.
 */
export const acceptNewItem = (
  suggestion: CollectionSuggestion, state: any, dispatch: any
): void => {
  switch (suggestion.section) {
    case "studies": {
      const item = suggestion.item ?? {};
      const study = {
        ...DEFAULT_STUDY, ...item,
        from: toDate("from", item.from, new Set(["from"])),
        to: toDate("to", item.to, new Set(["to"])),
      };
      dispatch({ type: "ADD_STUDY_ENTRY", payload: study });
      break;
    }
    case "workExperience": {
      const item = suggestion.item ?? {};
      const work = {
        ...DEFAULT_WORK, ...item,
        exp_from: toDate("exp_from", item.exp_from, new Set(["exp_from"])),
        exp_to: toDate("exp_to", item.exp_to, new Set(["exp_to"])),
      };
      dispatch({ type: "ADD_WORK_ENTRY", payload: work });
      break;
    }
    case "certificates": {
      dispatch({ type: "ADD_CERTIFICATE_FIELD",
        data: suggestion.item, index: state.certificates.length });
      break;
    }
    case "competences": {
      // All competence suggestions are child entries; set the rating on the
      // existing catalog child via a live-state action (no clone/overwrite).
      if (suggestion.item?.type === "child" && suggestion.item.child?.name) {
        dispatch({
          type: "SET_COMPETENCE_RATING",
          competenceName: suggestion.item.competenceName,
          childName: suggestion.item.child.name,
          details: suggestion.item.child.candidate_competence_details,
        });
      }
      break;
    }
    default:
      break;
  }
};

/** Human-readable label for a row in the review popup. */
export const describeNewItem = (s: CollectionSuggestion): string => {
  const i = s.item ?? {};
  switch (s.section) {
    case "studies":
      return [i.study, i.school].filter(Boolean).join(" — ") || "Study";
    case "workExperience":
      return [i.title, i.employer].filter(Boolean).join(" @ ") || "Experience";
    case "certificates":
      return [i.attestType, i.documentNo].filter(Boolean).join(" ") || "Certificate";
    case "competences":
      return i.type === "parent"
        ? i.competence?.competence_name ?? "Competence"
        : `${i.child?.name ?? "Skill"} (${i.competenceName ?? ""})`;
    default:
      return "Suggestion";
  }
};

/** Render any value (dropdown object / date / scalar) for display in the popup. */
export const displayValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === "object") {
    if ("label" in value) return String((value as any).label);
    if ("value" in value) return String((value as any).value);
    return JSON.stringify(value);
  }
  return String(value);
};
